/**
 * Animated markdown replace: "backspace" the old middle segment, then type the new one,
 * updating the whole editor via Markdown setContent (matches NeoAi structured edits).
 */

function jitter(ms) {
  return ms + Math.floor(Math.random() * 18);
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function setMd(editor, markdown, signal) {
  if (signal?.aborted) return false;
  editor.commands.setContent(markdown, {
    contentType: "markdown",
    parseOptions: { preserveWhitespace: false },
  });
  return true;
}

function markReveal(editor, on) {
  const dom = editor?.view?.dom;
  if (!dom || !(dom instanceof HTMLElement)) return;
  if (on) dom.setAttribute("data-neoai-reveal", "true");
  else dom.removeAttribute("data-neoai-reveal");
}

/**
 * @param {import("@tiptap/core").Editor} editor
 * @param {{
 *   prefix: string;
 *   middle: string;
 *   suffix: string;
 *   incoming: string;
 *   signal?: AbortSignal;
 *   maxEraseFrames?: number;
 *   maxTypeFrames?: number;
 * }} opts
 */
export async function revealMarkdownReplace(editor, opts) {
  const {
    prefix,
    middle,
    suffix,
    incoming,
    signal,
    maxEraseFrames = 72,
    maxTypeFrames = 96,
  } = opts;

  const mid = typeof middle === "string" ? middle : "";
  const inc = typeof incoming === "string" ? incoming : "";
  const pre = typeof prefix === "string" ? prefix : "";
  const suf = typeof suffix === "string" ? suffix : "";

  const merged = pre + inc + suf;

  if (!editor?.commands?.setContent) {
    return;
  }

  markReveal(editor, true);
  editor.view?.focus?.();

  try {
    if (signal?.aborted) {
      setMd(editor, merged, signal);
      return;
    }

    const eraseStep = Math.max(1, Math.ceil(mid.length / Math.max(8, maxEraseFrames)));
    let k = mid.length;
    while (true) {
      if (signal?.aborted) break;
      const shown = mid.slice(0, k);
      setMd(editor, pre + shown + suf, signal);
      editor.view?.focus?.();
      if (k === 0) break;
      k = Math.max(0, k - eraseStep);
      await sleep(jitter(10 + Math.min(28, Math.floor(eraseStep * 1.2))));
    }
    if (!signal?.aborted && mid.length > 0) {
      await sleep(jitter(36));
    }

    const typeStep = Math.max(1, Math.ceil(inc.length / Math.max(10, maxTypeFrames)));
    let t = 0;
    while (true) {
      if (signal?.aborted) break;
      const upto = Math.min(inc.length, t);
      setMd(editor, pre + inc.slice(0, upto) + suf, signal);
      editor.view?.focus?.();
      if (upto >= inc.length) break;
      t += typeStep;
      await sleep(jitter(8 + Math.min(22, Math.floor(typeStep * 0.9))));
    }

    setMd(editor, merged, signal);
    try {
      editor.chain().focus("end").run();
    } catch {
      /* ignore */
    }
    editor.view?.focus?.();
  } finally {
    markReveal(editor, false);
  }
}

/**
 * Grow appended markdown at the end of `base` (no erase phase).
 *
 * @param {import("@tiptap/core").Editor} editor
 * @param {string} baseMarkdown trimmed base without trailing gap
 * @param {string} appendMarkdown new block(s) to append (may include leading \\n\\n)
 * @param {{ signal?: AbortSignal; maxTypeFrames?: number }} [opts]
 */
export async function revealMarkdownAppend(editor, baseMarkdown, appendMarkdown, opts = {}) {
  const { signal, maxTypeFrames = 80 } = opts;
  const base = (baseMarkdown || "").replace(/\s*$/, "");
  const inc = typeof appendMarkdown === "string" ? appendMarkdown : "";
  const merged = `${base}\n\n${inc}`.trim();

  if (!editor?.commands?.setContent) return;

  markReveal(editor, true);
  editor.view?.focus?.();

  try {
    if (signal?.aborted || !inc) {
      setMd(editor, merged, signal);
      return;
    }

    const glue = base.length ? "\n\n" : "";
    const tail = `${glue}${inc}`;
    const typeStep = Math.max(1, Math.ceil(tail.length / Math.max(10, maxTypeFrames)));
    let t = 0;
    while (true) {
      if (signal?.aborted) break;
      const upto = Math.min(tail.length, t);
      setMd(editor, (base + tail.slice(0, upto)).trim(), signal);
      editor.view?.focus?.();
      if (upto >= tail.length) break;
      t += typeStep;
      await sleep(jitter(8 + Math.min(20, Math.floor(typeStep * 0.85))));
    }
    setMd(editor, merged, signal);
    editor.view?.focus?.();
  } finally {
    markReveal(editor, false);
  }
}
